import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';
import Grid from '@mui/material/Grid2';
import CachedIcon from '@mui/icons-material/Cached';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Card,
  Box,
  Typography,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import FormField from 'components/FormField';

const MAX_ROWS = 5;

const propTypes = {
  invoiceContent: PropTypes.object.isRequired,
  setInvoiceContent: PropTypes.func.isRequired,
  setPreviewContent: PropTypes.func.isRequired,
};

const DashboardBillingForReceivedTasksInvoiceEditorEditor = ({
  invoiceContent,
  setInvoiceContent,
  setPreviewContent,
}) => {
  const [dynamicFields, setDynamicFields] = useState([]);

  const handleChangeContent = (key) => async (event) => {
    setInvoiceContent((prevState) => {
      return {
        ...prevState,
        [key]: event.target.value,
      };
    });
  };

  const handleDynamicFieldChange = (rowIndex, fieldName, value) => {
    setDynamicFields((prevFields) => {
      const newFields = [...prevFields];
      if (!newFields[rowIndex]) {
        newFields[rowIndex] = {};
      }
      newFields[rowIndex][fieldName] = value;
      return newFields;
    });
  };

  const handleAddNewRow = () => {
    if (dynamicFields.length >= MAX_ROWS) {
      return;
    }

    setDynamicFields((prevFields) => [
      ...prevFields,
      { type: '', count: '', price: '' },
    ]);
  };

  const handleRemoveRow = (rowIndex) => {
    setDynamicFields((prevFields) => {
      const newFields = [...prevFields];
      newFields.splice(rowIndex, 1);
      return newFields;
    });
  };

  // TODO Add the data in here
  const handleRerenderButtonClick = () => {
    // Calculate the total amount
    const newTotalAmount = dynamicFields.reduce((acc, field) => {
      return acc + field.price * field.count;
    }, 0);

    setPreviewContent(() => ({
      ...invoiceContent,
      userAddedBills: dynamicFields,
      invoiceTotalAmount: invoiceContent.invoiceTotalAmount + newTotalAmount,
      invoiceTotalAmountIncludingTax:
        ((invoiceContent.invoiceTotalAmount + newTotalAmount) *
          (100 + Number(invoiceContent.invoiceTaxRate))) /
        100,
    }));
  };

  return (
    <Fragment>
      <Card square={true} sx={{ overflow: 'visible', height: '100%' }}>
        {/* Title */}
        <Box py={3} px={3} mt={1} mx={3}>
          <Box pl={1} mb={2} display="center" justifyContent="center">
            <Typography variant="h5">
              {intl.get('billing_invoice_editor_text_title')}
            </Typography>
          </Box>

          {/* Details */}
          <Grid container spacing={3}>
            <Grid size={6} style={{ marginTop: 15 }}>
              <FormField
                variant="outlined"
                label={intl.get('billing_invoice_editor_label_date')}
                onChange={handleChangeContent('invoiceDate')}
                value={invoiceContent.invoiceDate}
              />
            </Grid>

            {/* Actions */}
            <Grid size={6} style={{ marginTop: 15 }}>
              <Grid container justifyContent="flex-end">
                <IconButton onClick={handleRerenderButtonClick}>
                  <Tooltip
                    title={intl.get(
                      'issue_details_report_preview_text_rerender'
                    )}>
                    <CachedIcon />
                  </Tooltip>
                </IconButton>
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid size={6} style={{ marginTop: 15 }}>
              <FormField
                variant="outlined"
                label={intl.get('billing_invoice_editor_label_to')}
                onChange={handleChangeContent('invoiceTo')}
                value={invoiceContent.invoiceTo}
                multiline
              />
            </Grid>
            <Grid size={6} style={{ marginTop: 15 }}>
              <FormField
                variant="outlined"
                label={intl.get('billing_invoice_editor_label_to_rep_name')}
                onChange={handleChangeContent('invoiceTo2')}
                value={invoiceContent.invoiceTo2}
                multiline
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid size={12} style={{ marginTop: 15 }}>
              <FormField
                variant="outlined"
                label={intl.get('billing_invoice_editor_label_from')}
                onChange={handleChangeContent('invoiceFrom')}
                value={invoiceContent.invoiceFrom}
                multiline
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid size={12} style={{ marginTop: 15 }}>
              <FormField
                variant="outlined"
                label={intl.get('billing_invoice_editor_label_bill_title')}
                onChange={handleChangeContent('invoiceDetailedTitle')}
                value={invoiceContent.invoiceDetailedTitle}
                multiline
              />
            </Grid>
          </Grid>

          {/* Bill amount */}
          <Box py={1} px={1} mt={1} mx={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center">
              <Typography variant="h6">
                {intl.get('billing_invoice_editor_label_bill_related')}
              </Typography>
              <IconButton onClick={handleAddNewRow}>
                <Tooltip
                  title={intl.get('billing_invoice_editor_label_add_new_row')}>
                  <AddIcon />
                </Tooltip>
              </IconButton>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {dynamicFields.map((row, rowIndex) => (
              <Grid container key={rowIndex}>
                <Grid size={1}>
                  <IconButton onClick={() => handleRemoveRow(rowIndex)}>
                    <Tooltip
                      title={intl.get(
                        'billing_invoice_editor_label_remove_row'
                      )}>
                      <CloseIcon />
                    </Tooltip>
                  </IconButton>
                </Grid>
                <Grid size={5}>
                  <FormField
                    variant="outlined"
                    label={intl.get('billing_invoice_editor_label_item')}
                    onChange={(event) =>
                      handleDynamicFieldChange(
                        rowIndex,
                        'type',
                        event.target.value
                      )
                    }
                    value={row.type || ''}
                  />
                </Grid>
                <Grid size={3}>
                  <FormField
                    variant="outlined"
                    label={intl.get('billing_invoice_editor_label_count')}
                    onChange={(event) =>
                      handleDynamicFieldChange(
                        rowIndex,
                        'count',
                        event.target.value
                      )
                    }
                    value={row.count || ''}
                  />
                </Grid>
                <Grid size={3}>
                  <FormField
                    variant="outlined"
                    label={intl.get('billing_invoice_editor_label_price')}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="caption">￥</Typography>
                          </InputAdornment>
                        ),
                      },
                      htmlInput: {
                        min: 0, // Ensure `min` is passed to the underlying HTML <input>
                      },
                    }}
                    onChange={(event) =>
                      handleDynamicFieldChange(
                        rowIndex,
                        'price',
                        event.target.value
                      )
                    }
                    value={row.price || ''}
                  />
                </Grid>
              </Grid>
            ))}

            <Grid size={12}>
              <FormField
                variant="outlined"
                label={intl.get('billing_invoice_editor_label_tax_rate')}
                onChange={handleChangeContent('invoiceTaxRate')}
                value={invoiceContent.invoiceTaxRate}
              />
            </Grid>
          </Grid>

          {/* Payment method */}
          <Box py={1} px={1} mt={1} mx={1}>
            <Box>
              <Typography variant="h6">
                {intl.get('billing_invoice_editor_label_payment_method')}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid size={12} style={{ marginTop: 15 }}>
              <FormField
                variant="outlined"
                label={intl.get('billing_invoice_text_transfer_destination')}
                onChange={handleChangeContent('invoicePaymentMethod')}
                value={invoiceContent.invoicePaymentMethod}
                multiline
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid size={12} style={{ marginTop: 15 }}>
              <FormField
                variant="outlined"
                label={intl.get('billing_invoice_text_remarks')}
                onChange={handleChangeContent('invoiceRemarks')}
                value={invoiceContent.invoiceRemarks}
                multiline
              />
            </Grid>
          </Grid>
        </Box>
      </Card>
    </Fragment>
  );
};

DashboardBillingForReceivedTasksInvoiceEditorEditor.propTypes = propTypes;

export default DashboardBillingForReceivedTasksInvoiceEditorEditor;
